// lib/suggestions/telemetry.ts
//
// Telemetría de las sugerencias: carrusel del kiosko (PR 3) y hoja de la tienda online (PR 5c). Encola sobre el
// tracker del marketplace (cola + flush por timer, por tope o manual) y le
// agrega lo propio del kiosko:
//   · una impresión por (ítem, motivo) y SESIÓN, aunque el carrusel se
//     recalcule cada vez que cambia el carrito. El motivo entra en la clave
//     porque cambia con el carrito (una bebida es cross_sell sin plato y
//     missing_drink con uno): cada toque tiene que tener la impresión de su
//     motivo;
//   · el enlace sesión → pedido, una vez por pedido y solo si la sesión vio o
//     tocó alguna tarjeta (sin actividad no hay nada que atribuir);
//   · la sesión rota cuando el kiosko vuelve a la pantalla de atracción: un
//     cliente, una sesión.
//
// Regla dura, la misma del tracker: JAMÁS lanza. Es telemetría; un fallo acá
// no puede tumbar un toque, el carrito ni un cobro.
import { makeTracker, randomId, type Tracker, type TrackPayload } from '@/lib/marketplace/track'
import { MAX_CARD_INDEX, MAX_SCORE, type SuggestionEvent, type SuggestionEventBatch, type SuggestionMoment } from './events'
import type { SuggestionReason } from './types'

/** Lo mínimo que la telemetría necesita de una tarjeta. `Suggestion` (kiosko)
 *  es asignable; la tienda arma esto con el `score` que devuelve la acción. */
export type TelemetryCard = { item: { id: string }; reason: SuggestionReason; score: number }

export type SuggestionTelemetry = {
  /** La tarjeta está en el carrusel. De-duplicada por (ítem, motivo) dentro de la sesión. */
  impression: (suggestion: TelemetryCard, cardIndex: number, moment?: SuggestionMoment) => void
  /** El cliente tocó la tarjeta (agregue directo o abra el modal). */
  tap: (suggestion: TelemetryCard, cardIndex: number, moment?: SuggestionMoment) => void
  /** La sesión produjo este pedido. De-duplicado por pedido. */
  order: (orderId: string) => void
  /** El kiosko volvió a la atracción: manda lo pendiente y arranca sesión nueva. */
  endSession: () => void
  /** Manda lo pendiente ya (pagehide / unmount). */
  flush: () => void
  /** Id de la sesión vigente, o null si todavía no vio ni tocó ninguna tarjeta
   *  (misma regla que `order`: sin actividad no hay nada que atribuir). Lo
   *  guarda el stash del pago en otro host para enlazar el pedido al volver. */
  sessionId: () => string | null
}

type TelemetryDeps = {
  send: (payload: TrackPayload<SuggestionEvent>) => void
  /** Inyectable para tests. Default: uuid aleatorio. */
  newSessionId?: () => string
  flushDelayMs?: number
  maxQueue?: number
}

type Session = {
  id: string
  tracker: Tracker<SuggestionEvent>
  /** Claves `${itemId}:${reason}` ya impresas en esta sesión; el momento
   *  `item_added` se agrega como sufijo (`${itemId}:${reason}:item_added`). */
  seenCards: Set<string>
  linkedOrders: Set<string>
  /** true cuando la sesión ya vio o tocó alguna tarjeta. */
  active: boolean
}

/** Entero dentro de [0, max]; lo que no es número finito cae a 0. */
function clampInt(value: number, max: number): number {
  if (!Number.isFinite(value)) return 0
  return Math.max(0, Math.min(max, Math.round(value)))
}

export function makeSuggestionTelemetry({
  send,
  newSessionId = randomId,
  flushDelayMs,
  maxQueue,
}: TelemetryDeps): SuggestionTelemetry {
  function freshSession(): Session {
    const id = newSessionId()
    return {
      id,
      tracker: makeTracker<SuggestionEvent>({ sessionId: id, send, flushDelayMs, maxQueue }),
      seenCards: new Set(),
      linkedOrders: new Set(),
      active: false,
    }
  }

  let session = freshSession()

  function card(event: 'impression' | 'tap', suggestion: TelemetryCard, cardIndex: number, moment: SuggestionMoment): void {
    // Primero se arma el evento (una sugerencia malformada lanza acá) y la
    // sesión se marca activa solo si el evento se encoló: un toque roto no
    // puede habilitar el enlace de un pedido que nada sugirió.
    const tracked: SuggestionEvent = {
      event,
      item_id: suggestion.item.id,
      reason: suggestion.reason,
      card_index: clampInt(cardIndex, MAX_CARD_INDEX),
      score: clampInt(suggestion.score, MAX_SCORE),
      // Solo el momento nuevo viaja: el carrito es el default de la columna, y
      // así un evento del carrusel tiene la misma forma que antes del PR 6.
      ...(moment === 'item_added' ? { moment } : {}),
    }
    session.tracker.track(tracked)
    session.active = true
  }

  return {
    impression(suggestion, cardIndex, moment = 'cart_review') {
      try {
        // El momento entra en la clave: la misma bebida vista en la hoja y
        // después en el carrito son dos impresiones distintas.
        const key = moment === 'cart_review' ? `${suggestion.item.id}:${suggestion.reason}` : `${suggestion.item.id}:${suggestion.reason}:${moment}`
        if (session.seenCards.has(key)) return
        card('impression', suggestion, cardIndex, moment)
        session.seenCards.add(key)
      } catch {
        // jamás throw
      }
    },
    tap(suggestion, cardIndex, moment = 'cart_review') {
      try {
        card('tap', suggestion, cardIndex, moment)
      } catch {
        // jamás throw
      }
    },
    order(orderId) {
      try {
        if (typeof orderId !== 'string' || orderId.length === 0) return
        if (!session.active || session.linkedOrders.has(orderId)) return
        session.linkedOrders.add(orderId)
        session.tracker.track({ event: 'order', order_id: orderId })
      } catch {
        // jamás throw
      }
    },
    endSession() {
      try {
        session.tracker.flush()
        session = freshSession()
      } catch {
        // jamás throw
      }
    },
    flush() {
      try {
        session.tracker.flush()
      } catch {
        // jamás throw
      }
    },
    sessionId() {
      try {
        return session.active ? session.id : null
      } catch {
        return null
      }
    },
  }
}

export type TokenCell = {
  /** El token vigente; null hasta que alguien lo escriba. */
  get: () => string | null
  set: (token: string) => void
}

/**
 * Casilla mutable para el token del kiosko. El hook crea la telemetría UNA vez
 * por local y le va escribiendo el token que llega en cada render del servidor
 * (router.refresh() lo renueva sin recargar); el sender lo lee en cada envío.
 */
export function makeTokenCell(): TokenCell {
  let token: string | null = null
  return {
    get: () => token,
    set: (next) => {
      token = next
    },
  }
}

type SenderDeps = {
  locationId: string
  /** Se lee en cada envío. null o vacío ⇒ no se manda nada. */
  getToken: () => string | null
  url?: string
  /** Inyectable para tests. Default: fetch global. */
  fetchFn?: typeof fetch
}

/**
 * Transporte del lote hacia la ruta del kiosko. `fetch keepalive` y no
 * `sendBeacon`: la ruta exige `X-POS-Auth` y sendBeacon no manda cabeceras.
 * Nunca lanza y nadie lee la respuesta.
 */
export function makeKioskEventSender({
  locationId,
  getToken,
  url = '/api/kiosk/suggestion-events',
  fetchFn,
}: SenderDeps): (payload: TrackPayload<SuggestionEvent>) => void {
  return (payload) => {
    try {
      const doFetch = fetchFn ?? (typeof fetch !== 'undefined' ? fetch : undefined)
      if (!doFetch) return
      const token = getToken()
      if (!token) return
      const batch: SuggestionEventBatch = {
        session_id: payload.session_id,
        location_id: locationId,
        events: payload.events,
      }
      void doFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-POS-Auth': token },
        body: JSON.stringify(batch),
        keepalive: true,
      }).catch(() => {
        // best-effort: el lote se pierde y ya
      })
    } catch {
      // jamás throw
    }
  }
}

type PublicSenderDeps = {
  locationId: string
  url?: string
  /** Inyectable para tests. Default: navigator.sendBeacon si existe. */
  beacon?: (url: string, data: BodyInit) => boolean
  fetchFn?: typeof fetch
}

/**
 * Transporte hacia la ruta PÚBLICA de la tienda (sin token). `sendBeacon`
 * primero —el toque que abre el carrito y el enlace del pedido pasan justo
 * antes de navegaciones— y `fetch keepalive` de respaldo. Nunca lanza, nadie
 * lee la respuesta (la ruta responde 204 pase lo que pase).
 */
export function makePublicEventSender({
  locationId,
  url = '/api/online/suggestion-events',
  beacon,
  fetchFn,
}: PublicSenderDeps): (payload: TrackPayload<SuggestionEvent>) => void {
  return (payload) => {
    try {
      const batch: SuggestionEventBatch = { session_id: payload.session_id, location_id: locationId, events: payload.events }
      const body = JSON.stringify(batch)
      const doBeacon =
        beacon ??
        (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function'
          ? navigator.sendBeacon.bind(navigator)
          : undefined)
      if (doBeacon) {
        try {
          const data = typeof Blob !== 'undefined' ? new Blob([body], { type: 'application/json' }) : body
          if (doBeacon(url, data)) return
        } catch {
          // cae al fetch
        }
      }
      const doFetch = fetchFn ?? (typeof fetch !== 'undefined' ? fetch : undefined)
      if (!doFetch) return
      void doFetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {
        // best-effort
      })
    } catch {
      // jamás throw
    }
  }
}
